import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { buildingUtilityApi } from '../../api/buildingUtilityApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { BuildingUtilityGroup, UtilityType, UtilityGroupStatus } from '../../types';
import { useCrudPermissions } from '../../hooks/useCrudPermissions';
import { BUILDING_UTILITY_GROUP_PERMISSIONS } from '../../constants/functionPermissions';

export const BuildingUtilityGroups: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const { canCreate, canEdit, canDelete } = useCrudPermissions(BUILDING_UTILITY_GROUP_PERMISSIONS);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [groups, setGroups] = useState<BuildingUtilityGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingDefault, setCreatingDefault] = useState(false);

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      loadGroups();
    } else {
      setGroups([]);
    }
  }, [selectedBuildingId]);

  const loadBuildings = async () => {
    try {
      const response = await buildingsApi.getBuildings();
      if (response && Array.isArray(response)) {
        setBuildings(response);
        setSelectedBuildingId((prev) => applyDefaultBuildingSelection(response, user, prev));
      }
    } catch (err: any) {
      setError('Error loading buildings');
    }
  };

  const loadGroups = async () => {
    if (!selectedBuildingId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await buildingUtilityApi.getUtilityGroups(selectedBuildingId, {
        page: 1,
        limit: 200,
      });
      
      if (response && Array.isArray(response.data)) {
        setGroups(response.data);
      } else {
        console.warn('Expected data with array but got:', response);
        setGroups([]);
        setError('Invalid data format received from server');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading utility groups');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaultGroups = async () => {
    if (!selectedBuildingId) {
      alert('Vui lòng chọn tòa nhà');
      return;
    }

    if (!window.confirm('Tạo các nhóm tiện ích mặc định? Các nhóm đã tồn tại sẽ được bỏ qua, chỉ bổ sung nhóm còn thiếu.')) {
      return;
    }

    try {
      setCreatingDefault(true);
      const result = await buildingUtilityApi.createDefaultUtilityGroups(selectedBuildingId);
      await loadGroups();
      const created = (result as any)?.count ?? 0;
      const skipped = (result as any)?.skippedCount ?? 0;
      alert(
        created > 0
          ? `Đã tạo ${created} nhóm mới${skipped > 0 ? `, bỏ qua ${skipped} nhóm đã có` : ''}.`
          : skipped > 0
            ? `Tất cả nhóm mặc định đã tồn tại (${skipped} nhóm).`
            : 'Đã tạo thành công các nhóm tiện ích mặc định!',
      );
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error creating default groups');
    } finally {
      setCreatingDefault(false);
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: UtilityGroupStatus) => {
    try {
      await buildingUtilityApi.toggleUtilityGroupStatus(id);
      await loadGroups();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error toggling group status');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Xóa nhóm tiện ích "${name}"?`)) {
      return;
    }

    try {
      await buildingUtilityApi.deleteUtilityGroup(id);
      await loadGroups();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting group');
    }
  };

  const getUtilityTypeLabel = (type: UtilityType) => {
    switch (type) {
      case UtilityType.NO_BOOKING:
        return 'Không đặt trước';
      case UtilityType.TIME_BASED:
        return 'Thời gian';
      case UtilityType.FIXED_SLOTS:
        return 'Khung thời gian';
      case UtilityType.NOTIFICATION_ONLY:
        return 'Đăng ký sử dụng';
      default:
        return type;
    }
  };

  const getStatusBadge = (status: UtilityGroupStatus) => {
    if (status === UtilityGroupStatus.ACTIVE) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Hoạt động
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Không hoạt động
        </span>
      );
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>Tiện ích tòa nhà</span> / <span className="text-gray-900">Danh sách nhóm tiện ích</span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Danh sách nhóm tiện ích</h1>
        <p className="text-gray-600">Quản lý các nhóm tiện ích trong tòa nhà</p>
      </div>

      {/* Building Selection */}
      {shouldShowBuildingSelector(buildings) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Chọn tòa nhà:</label>
            <select
              value={selectedBuildingId}
              onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Chọn tòa nhà --</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {selectedBuildingId && (
        <>
          {/* Actions Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-4">
                {canCreate && (
                  <>
                    <button
                      onClick={handleCreateDefaultGroups}
                      disabled={creatingDefault}
                      className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      {creatingDefault ? 'Đang tạo...' : 'Tạo nhóm tiện ích mặc định'}
                    </button>
                    
                    <Link
                      to={`/building-utility-groups/add?buildingId=${selectedBuildingId}`}
                      className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 shadow-md"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Tạo nhóm tiện ích
                    </Link>
                  </>
                )}
              </div>

              <div className="text-sm text-gray-500">
                Tổng: <span className="font-semibold text-gray-900">{groups.length}</span> nhóm tiện ích
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-800">{error}</span>
                <button
                  onClick={loadGroups}
                  className="ml-auto px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                >
                  Thử lại
                </button>
              </div>
            </div>
          )}

          {/* Groups Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="text-lg text-gray-600">Đang tải danh sách nhóm tiện ích...</div>
              </div>
            ) : groups.length === 0 ? (
              <div className="p-8 text-center">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có nhóm tiện ích nào</h3>
                <p className="text-gray-500 mb-4">Bắt đầu bằng cách tạo nhóm tiện ích mặc định hoặc tạo nhóm mới</p>
                <div className="flex gap-4 justify-center">
                  {canCreate && (
                    <>
                      <button
                        onClick={handleCreateDefaultGroups}
                        disabled={creatingDefault}
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-200 disabled:opacity-50"
                      >
                        Tạo nhóm mặc định
                      </button>
                      <Link
                        to={`/building-utility-groups/add?buildingId=${selectedBuildingId}`}
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200"
                      >
                        Tạo nhóm mới
                      </Link>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        STT
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tên nhóm
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Loại tiện ích
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Thứ tự hiển thị
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trạng thái
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ngày tạo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {groups.map((group, index) => (
                      <tr key={group.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center text-white font-medium text-lg">
                              {group.icon || '🏢'}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{group.name}</div>
                              {group.nameEn && (
                                <div className="text-sm text-gray-500">{group.nameEn}</div>
                              )}
                              {group.description && (
                                <div className="text-xs text-gray-400 mt-1">{group.description}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getUtilityTypeLabel(group.utilityType)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {group.displayOrder}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(group.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(group.createdAt).toLocaleDateString('vi-VN')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {canEdit && (
                              <>
                                <Link
                                  to={`/building-utility-groups/${group.id}/edit`}
                                  className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </Link>
                                
                                <button
                                  onClick={() => handleToggleStatus(group.id, group.status)}
                                  className={`transition-colors ${
                                    group.status === UtilityGroupStatus.ACTIVE
                                      ? 'text-red-600 hover:text-red-900'
                                      : 'text-green-600 hover:text-green-900'
                                  }`}
                                  title={group.status === UtilityGroupStatus.ACTIVE ? 'Vô hiệu hóa' : 'Kích hoạt'}
                                >
                                  {group.status === UtilityGroupStatus.ACTIVE ? (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </button>
                              </>
                            )}

                            {canDelete && (
                              <button
                                onClick={() => handleDelete(group.id, group.name)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                title="Xóa"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}; 